import axios from 'axios';
import { toast } from 'sonner';
import { authReadyPromise } from './authReady';
import { getBackendUrl } from './runtimeConfig';

const API_URL = getBackendUrl() + '/api';

// Bloquer les appels API tant que l'auth n'est pas initialisée
axios.interceptors.request.use(async (config) => {
  const isAuthEndpoint = config.url?.includes('/auth/');
  if (!isAuthEndpoint) {
    await authReadyPromise;
  }
  return config;
});

// Centralized response error handling
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (!error.response) {
      // Network error / server unreachable
      // Skip toast for requests marked as silent (non-critical background fetches)
      if (navigator.onLine && !error.config?._silent) {
        toast.error('Erreur réseau', { description: 'Le serveur est inaccessible.' });
      }
    } else if (error.response.status === 401) {
      // Unauthorized – clear session and redirect to login
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Furniture API
export const furnitureApi = {
  getAll: (params = {}) => axios.get(`${API_URL}/furniture`, { params }),
  getById: (id) => axios.get(`${API_URL}/furniture/${id}`),
  getByBarcode: (barcode) => axios.get(`${API_URL}/furniture/by-barcode/${barcode}`),
  create: (data) => axios.post(`${API_URL}/furniture`, data),
  update: (id, data) => axios.patch(`${API_URL}/furniture/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/furniture/${id}`),
  count: () => axios.get(`${API_URL}/furniture/count`),
};

// Furniture Types API
export const furnitureTypesApi = {
  getAll: (includeInactive = false) => axios.get(`${API_URL}/furniture-types`, { params: { include_inactive: includeInactive } }),
  getById: (id) => axios.get(`${API_URL}/furniture-types/${id}`),
  create: (data) => axios.post(`${API_URL}/furniture-types`, data),
  update: (id, data) => axios.patch(`${API_URL}/furniture-types/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/furniture-types/${id}`),
  toggle: (id) => axios.post(`${API_URL}/furniture-types/${id}/toggle`),
};

// Locations API
export const locationsApi = {
  getAll: (params = {}) => axios.get(`${API_URL}/locations`, { params }),
  getTree: () => axios.get(`${API_URL}/locations/tree`),
  getSites: () => axios.get(`${API_URL}/locations/sites`),
  getByParent: (parentId) => axios.get(`${API_URL}/locations/by-parent/${parentId}`),
  getById: (id) => axios.get(`${API_URL}/locations/${id}`),
  create: (data) => axios.post(`${API_URL}/locations`, data),
  update: (id, data) => axios.patch(`${API_URL}/locations/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/locations/${id}`),
};

// Movements API
export const movementsApi = {
  getAll: (params = {}) => axios.get(`${API_URL}/movements`, { params }),
  getByFurniture: (furnitureId) => axios.get(`${API_URL}/movements/furniture/${furnitureId}`),
  create: (data) => axios.post(`${API_URL}/movements`, data),
};

// Inventory API
export const inventoryApi = {
  getAll: (params = {}) => axios.get(`${API_URL}/inventory`, { params }),
  getById: (id) => axios.get(`${API_URL}/inventory/${id}`),
  create: (data) => axios.post(`${API_URL}/inventory`, data),
  scan: (id, data) => axios.post(`${API_URL}/inventory/${id}/scan`, data),
  complete: (id) => axios.post(`${API_URL}/inventory/${id}/complete`),
  getResults: (id) => axios.get(`${API_URL}/inventory/${id}/results`),
  getReport: (id, params = {}) => axios.get(`${API_URL}/inventory/${id}/report`, { params }),
  downloadReportPdf: (id, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = `${API_URL}/inventory/${id}/report/pdf${queryString ? '?' + queryString : ''}`;
    return url;
  },
};

// Dashboard API
export const dashboardApi = {
  getStats: () => axios.get(`${API_URL}/dashboard/stats`),
};

// Reports API
export const reportsApi = {
  export:   (params = {}) => axios.get(`${API_URL}/reports/export`, { params }),
  getStats: (params = {}) => axios.get(`${API_URL}/reports/stats`,  { params }),
};

// Exports Comptables API
export const exportsApi = {
  getImmobilisationsUrl: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return `${API_URL}/exports/immobilisations${queryString ? '?' + queryString : ''}`;
  },
  getMouvementsUrl: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return `${API_URL}/exports/mouvements${queryString ? '?' + queryString : ''}`;
  },
  getInventairesUrl: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return `${API_URL}/exports/inventaires${queryString ? '?' + queryString : ''}`;
  },
};

// Auth API
export const authApi = {
  changePassword: (data) => axios.put(`${API_URL}/auth/password`, data),
};

// Users API
export const usersApi = {
  getAll: () => axios.get(`${API_URL}/users`),
  updateRole: (id, role) => axios.patch(`${API_URL}/users/${id}/role`, null, { params: { role } }),
  toggleStatus: (id) => axios.patch(`${API_URL}/users/${id}/status`),
};

// Catalog API
export const catalogApi = {
  getAll: (params = {}) => axios.get(`${API_URL}/catalog`, { params }),
  getById: (id) => axios.get(`${API_URL}/catalog/${id}`),
  create: (data) => axios.post(`${API_URL}/catalog`, data),
  update: (id, data) => axios.patch(`${API_URL}/catalog/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/catalog/${id}`),
  getTypes: () => axios.get(`${API_URL}/catalog/types`),
  getBrands: () => axios.get(`${API_URL}/catalog/brands`),
  getSuppliers: () => axios.get(`${API_URL}/catalog/suppliers`),
  uploadImage: (itemId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/catalog/upload-image/${itemId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Import API
export const importApi = {
  getTemplate: () => axios.get(`${API_URL}/import/template`),
  preview: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/import/preview`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  execute: (file, skipDuplicates = true) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/import/execute?skip_duplicates=${skipDuplicates}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  catalog: (file, skipDuplicates = true) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/import/catalog?skip_duplicates=${skipDuplicates}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  locations: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/import/locations`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Export API
export const exportApi = {
  furniture: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = `${API_URL}/export/furniture${queryString ? '?' + queryString : ''}`;
    return axios.get(url, { responseType: 'blob' });
  },
  movements: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = `${API_URL}/export/movements${queryString ? '?' + queryString : ''}`;
    return axios.get(url, { responseType: 'blob' });
  },
  inventory: (inventoryId) => axios.get(`${API_URL}/export/inventory/${inventoryId}`, { responseType: 'blob' }),
  catalog: () => axios.get(`${API_URL}/export/catalog`, { responseType: 'blob' }),
  locations: () => axios.get(`${API_URL}/export/locations`, { responseType: 'blob' })
};
